import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { inputBaseClasses } from '@mui/material/InputBase'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import { fetchModulesCalled, fetchModulesSave } from '../OfferPackage/state/actions'
import { makeStyles } from '@mui/styles'
import { IRootReducerState } from 'state/reducers'
import { Operatingunit } from '@cbgms/api'

interface Module {
  ModuleId: string
  ModuleName: string
  IsSearchFilter: boolean
  AppId: string
  TopMotiveFlag: number
  IsBasePackage: boolean
  IsLoginAccess: boolean
  FunctionalRequirementModuleStore: string
  ModuleStoreFlag: number | null
  ERP: 'Yes' | 'No'
}

const useStyles = makeStyles({
  moduleText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  moduleTitle: {
    marginBottom: '2px',
    marginLeft: '18px',
    color: '#808080',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  backButton: {
    alignItems: 'flex-start'
  },
  saveButton: {
    marginBottom: '1px'
  },
  tableHeaderCell: {
    padding: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#808080',
    whiteSpace: 'nowrap'
  },
  tableHeaderCellCenter: {
    padding: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#808080',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  tableCell: {
    fontSize: 14,
    padding: '4px',
    color: '#808080'
  },
  tableCellCenter: {
    fontSize: 14,
    padding: '4px',
    color: '#808080',
    textAlign: 'center'
  },
  textField: {
    width: '100px'
  },
  tableRowOdd: {
    backgroundColor: 'action.hover'
  },
  tableRowLast: {
    border: 0
  },
  paperContainer: {
    backgroundColor: 'white',
    marginTop: '5px'
  },
  tableContainer: {
    padding: 16,
    backgroundColor: 'white',
    marginTop: '5px',
    overflowY: 'auto',
    height: '100%'
  },
  moduleManagementTitle: {
    marginBottom: 0,
    fontSize: 20,
    fontWeight: 700
  },
  arrowIcon: {
    marginRight: '0.5em'
  },
  tableRowOddStyle: {
    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
    '&:last-child td, &:last-child th': { border: 0 }
  },
  tableCellWordBreak: {
    wordBreak: 'break-word'
  }
})

const ModuleManagement: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const tableRef = React.useRef<HTMLDivElement>(null)

  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [originalModules, setOriginalModules] = useState<Module[]>([])
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const [tableHeight, setTableHeight] = useState<number>(window.innerHeight)
  const [inputValue, setInputValue] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const ou_name = useSelector((state: IRootReducerState) => state.auth.user.ou_name) || Operatingunit.ModelOu
  const fetchModules = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const modualeData = await dispatch(fetchModulesCalled(ou_name))
      setLoading(false)
      setModules(modualeData.Data)
      setOriginalModules(modualeData.Data)
    } catch (error) {
      setError(t('module.fetchError'))
      notifier.error(t('module.fetchError'))
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }, [dispatch, notifier])

  const handleResize = useCallback(() => {
    if (tableRef.current) {
      setTableHeight(window.innerHeight - tableRef.current.getBoundingClientRect().top - 20)
    }
  }, [])

  const handleCheckboxChange = useCallback((ModuleId: string, field: keyof Module) => {
    setModules(prevModules => prevModules.map(module => (module.ModuleId === ModuleId ? { ...module, [field]: !module[field] } : module)))
    setIsChanged(true)
  }, [])

  const handleInputChange = useCallback((ModuleId: string, field: keyof Module, value: string) => {
    if ((!isNaN(Number(value)) || value === '') && value.length <= 10) {
      setModules(prevModules =>
        prevModules.map(module =>
          module.ModuleId === ModuleId
            ? {
                ...module,
                [field]: value === '' ? null : field === 'ModuleStoreFlag' || field === 'TopMotiveFlag' ? Number(value) : value
              }
            : module
        )
      )
      setIsChanged(true)
    }
  }, [])

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    try {
      const changedModules = modules.filter(
        (module, index) =>
          module.IsSearchFilter !== originalModules[index].IsSearchFilter ||
          module.TopMotiveFlag !== originalModules[index].TopMotiveFlag ||
          module.IsBasePackage !== originalModules[index].IsBasePackage ||
          module.ModuleStoreFlag !== originalModules[index].ModuleStoreFlag ||
          module.IsLoginAccess !== originalModules[index].IsLoginAccess
      )

      const formattedData = {
        Modules: changedModules.map(module => ({
          AppId: module.AppId,
          IsBasePackage: module.IsBasePackage,
          IsSearchFilter: module.IsSearchFilter,
          ModuleId: module.ModuleId,
          ModuleName: module.ModuleName,
          ModuleStoreFlag: module.ModuleStoreFlag,
          TopMotiveFlag: module.TopMotiveFlag,
          IsLoginAccess: module.IsLoginAccess
        }))
      }

      const modualeSaveData = await dispatch(fetchModulesSave(formattedData))

      if (modualeSaveData.Data === 'SUCCESS') {
        fetchModules()
        notifier.success(t('mdoule.savedSucess')) // to do rrg
        setIsChanged(false)
        setLoading(false)
      } else {
        throw new Error(t('module.saveFail'))
      }
    } catch (error) {
      setError(t('module.saveError'))
      notifier.error(t('module.saveError'))
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchModules()
  }, [fetchModules])

  const handleSearch = () => {
    setSearchTerm(inputValue.trim())
  }

  const filteredModules = searchTerm
    ? modules.filter(
        m => m.ModuleId.toLowerCase().includes(searchTerm.toLowerCase()) || m.ModuleName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : modules

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  return (
    <>
      <Box data-testid='moduleText' className={classes.moduleText}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography variant='h6' className={classes.moduleManagementTitle}>
            {t('module.ModuleManagement')}
          </Typography>
          <TextField
            size='small'
            placeholder={t('module.SearchPlaceholder')}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            sx={{ width: '300px', [`& .${inputBaseClasses.root}`]: { height: '35px', fontSize: '14px' } }}
            inputProps={{ 'aria-label': t('module.SearchPlaceholder') }}
          />
          <Button variant='contained' className='ui blue tiny button ml-half' onClick={handleSearch} style={{ height: '35px' }}>
            <i className='search icon' />
            {t('module.Search')}
          </Button>
        </Box>
        <Box>
          <Link to='/'>
            <Button variant='outlined' color='inherit' className={`ui tiny ml-half ${classes.backButton}`}>
              <i aria-hidden='true' className={`arrow left icon ${classes.arrowIcon}`} />
              {t('module.Back')}
            </Button>
          </Link>

          <Button
            type='submit'
            variant='contained'
            data-testid='savebtn'
            className={`ui tiny primary button ml-half ${classes.saveButton}`}
            onClick={handleSave}
            disabled={!isChanged}
          >
            <i className='save icon' />
            {t('module.Save')}
          </Button>
        </Box>
      </Box>

      <Paper ref={tableRef} className={classes.paperContainer} style={{ height: tableHeight }}>
        <Box className={classes.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color='error'>{error}</Typography>
          ) : (
            <TableContainer component={Box}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ borderBottom: '2px solid #808080' }}>
                    <TableCell className={classes.tableHeaderCell}>{t('module.ModuleID')}</TableCell>
                    <TableCell className={classes.tableHeaderCell}>{t('module.Description')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('module.IncludeinSearchLOV')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('module.EcommerceFlag')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('module.ModuleStoreFlag')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('module.BasePackage')}</TableCell>
                    <TableCell className={classes.tableHeaderCellCenter}>{t('module.IsLoginAccess')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModules.map(module => (
                    <TableRow key={module.ModuleId} className={classes.tableRowOddStyle}>
                      <TableCell className={classes.tableCell}>{module.ModuleId}</TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.tableCellWordBreak}`}>
                        {module.ModuleName.length > 15 ? (
                          <>
                            {module.ModuleName.slice(0, 100)}
                            <br />
                            {module.ModuleName.slice(100)}
                          </>
                        ) : (
                          module.ModuleName
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Checkbox
                          checked={module.IsSearchFilter}
                          onChange={() => handleCheckboxChange(module.ModuleId, 'IsSearchFilter')}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <TextField
                            value={module.TopMotiveFlag !== null ? module.TopMotiveFlag.toString() : ''}
                            onChange={e => handleInputChange(module.ModuleId, 'TopMotiveFlag', e.target.value)}
                            variant='outlined'
                            size='small'
                            className={classes.textField}
                          />
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <TextField
                            value={module.ModuleStoreFlag !== null ? module.ModuleStoreFlag.toString() : ''}
                            onChange={e => handleInputChange(module.ModuleId, 'ModuleStoreFlag', e.target.value)}
                            variant='outlined'
                            size='small'
                            className={classes.textField}
                          />
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Checkbox
                          checked={module.IsBasePackage}
                          onChange={() => handleCheckboxChange(module.ModuleId, 'IsBasePackage')}
                          disabled={module.ERP === 'No'}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Checkbox checked={module.IsLoginAccess} onChange={() => handleCheckboxChange(module.ModuleId, 'IsLoginAccess')} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
    </>
  )
}

export default ModuleManagement
